import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "@/lib/api-client";
import { useAuthStore, type User } from "@/stores/authStore";

type ExchangeResponse = {
  accessToken: string;
  user: User;
  impersonationLogId: string;
};

export function ImpersonationExchangePage() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);
  const started = useRef(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const fragment = new URLSearchParams(window.location.hash.slice(1));
    const exchangeCode = fragment.get("exchangeCode");
    window.history.replaceState(null, "", window.location.pathname);
    if (!exchangeCode) {
      setError("The one-time impersonation link is missing or invalid.");
      return;
    }

    apiClient
      .post<ExchangeResponse>("/admin/impersonation/exchange", {
        exchangeCode,
      })
      .then(({ data }) => {
        setAuth(data.user, data.accessToken, "");
        navigate("/dashboard", { replace: true });
      })
      .catch((exchangeError) => {
        setError(
          exchangeError?.response?.data?.message ||
            "This one-time impersonation link has expired or was already used.",
        );
      });
  }, [navigate, setAuth]);

  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-950 px-6">
      <section className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-2xl">
        <p className="text-xs font-semibold uppercase tracking-[0.24em] text-amber-600">
          Scoped support access
        </p>
        <h1 className="mt-3 text-2xl font-semibold text-slate-900">
          {error ? "Session could not be opened" : "Opening tenant session…"}
        </h1>
        <p className="mt-3 text-sm text-slate-500">
          {error ||
            "The one-time code is being exchanged for a short-lived, revocable session."}
        </p>
        {error && (
          <button
            type="button"
            onClick={() => navigate("/login", { replace: true })}
            className="mt-6 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
          >
            Return to login
          </button>
        )}
      </section>
    </main>
  );
}
